import { useState, useEffect } from "react";
import { usePlaidLink } from "react-plaid-link";
import { track } from "../../lib/track";
import { BUDGET_CATS, Transaction, getMonthLabel, shiftMonth, computeHealth } from "../../data/budgetCats";
import { useAuth } from "../../lib/useAuth";
import { supabase } from "../../lib/supabase";

const API = `${import.meta.env.VITE_API_URL}/api/plaid`;

type Modal = null | "logSpend" | "setBudget" | "editCatBudget" | "setIncome";

function fmt(n: number) { return "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtFull(n: number) { return "$" + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function pct(a: number, b: number) { return b > 0 ? Math.round((a / b) * 100) : 0; }
function newId() { return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`; }

export default function ProBudgetScreen({ budgetData, onSave }: { budgetData: any; onSave: (d: any) => void }) {
  const today = new Date().toISOString().slice(0, 7);
  const todayFull = new Date().toISOString().slice(0, 10);
  const [viewMonth, setViewMonth] = useState(today);
  const [modal, setModal]         = useState<Modal>(null);

  // ── Plaid ──────────────────────────────────────────────────────────────────
  const { session } = useAuth();
  const [linkToken,   setLinkToken]   = useState<string | null>(null);
  const [plaidBusy,   setPlaidBusy]   = useState(false);
  const [plaidLinked, setPlaidLinked] = useState(false);
  const [plaidError,  setPlaidError]  = useState<string | null>(null);

  function mapPlaidCat(primary: string): string {
    const p = (primary ?? "").toLowerCase().replace(/_/g, " ");
    for (const c of BUDGET_CATS) {
      if (c.plaidCategories.some((pc: string) => p.includes(pc.toLowerCase()))) return c.id;
    }
    return "other";
  }

  async function syncTransactions() {
    if (!supabase) return;
    const { data } = await supabase
      .from("transactions")
      .select("id, amount, date, merchant_name, name, personal_finance_category")
      .order("date", { ascending: false })
      .limit(200);
    if (!data?.length) return;
    const incoming: Transaction[] = data.map((t: any) => ({
      id:       t.id,
      date:     t.date,
      amount:   Math.abs(t.amount),
      category: mapPlaidCat(t.personal_finance_category?.primary ?? ""),
      merchant: t.merchant_name ?? t.name ?? "Bank transaction",
      source:   "plaid" as const,
    }));
    const forMonth   = incoming.filter(t => t.date.startsWith(viewMonth));
    const existingIds = new Set(transactions.map(t => t.id));
    const fresh       = forMonth.filter(t => !existingIds.has(t.id));
    if (fresh.length > 0) persistMonth({ transactions: [...transactions, ...fresh] });
  }

  async function handleConnectBank() {
    if (!session?.access_token) { setPlaidError("You must be signed in to connect a bank."); return; }
    setPlaidBusy(true);
    setPlaidError(null);
    try {
      const res  = await fetch(`${API}/link-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.link_token) setLinkToken(data.link_token);
      else setPlaidError(data.error ?? "Could not start bank connection — try again.");
    } catch {
      setPlaidError("Network error. Check your connection and try again.");
    } finally {
      setPlaidBusy(false);
    }
  }

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken ?? "",
    onSuccess: async (publicToken) => {
      if (!session?.access_token) return;
      setPlaidBusy(true);
      try {
        await fetch(`${API}/exchange-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
          body: JSON.stringify({ public_token: publicToken }),
        });
        await syncTransactions();
        setPlaidLinked(true);
        track("bank_linked");
        setModal(null);
      } catch {
        setPlaidError("Sync failed — please try again.");
      } finally {
        setPlaidBusy(false);
        setLinkToken(null);
      }
    },
    onExit: () => { setLinkToken(null); setPlaidBusy(false); },
  });

  useEffect(() => {
    if (linkToken && plaidReady) openPlaid();
  }, [linkToken, plaidReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Log-spend form
  const [logCat,      setLogCat]      = useState(BUDGET_CATS[0].id);
  const [logAmt,      setLogAmt]      = useState("");
  const [logMerchant, setLogMerchant] = useState("");

  // Set-budget form — keyed by category id
  const [editBudgets, setEditBudgets] = useState<Record<string, string>>({});

  // Single-category budget edit
  const [editCatId,  setEditCatId]  = useState<string>("");
  const [editCatAmt, setEditCatAmt] = useState<string>("");

  // Income form
  const [incomeInput, setIncomeInput] = useState("");

  const months: Record<string, any> = budgetData?.months ?? {};
  const monthData = months[viewMonth] ?? null;
  const cats      = monthData?.categories ?? {};
  const income: number = monthData?.income ?? 0;
  const transactions: Transaction[] = monthData?.transactions ?? [];

  function getCatActual(catId: string) {
    return transactions
      .filter(t => t.category === catId)
      .reduce((s, t) => s + t.amount, 0);
  }

  // Build computed cats (budget from stored data, actual derived from transactions)
  const computedCats = Object.fromEntries(
    BUDGET_CATS.map(c => [c.id, { budget: cats[c.id]?.budget ?? 0, actual: getCatActual(c.id) }])
  );

  function persistMonth(patch: any) {
    onSave({ ...budgetData, months: { ...months, [viewMonth]: { ...(monthData ?? {}), ...patch } } });
  }

  function saveIncome() {
    const val = Math.max(0, parseFloat(incomeInput) || 0);
    persistMonth({ income: val });
    setModal(null);
  }

  function openEditCat(catId: string) {
    setEditCatId(catId);
    setEditCatAmt(String(cats[catId]?.budget ?? BUDGET_CATS.find(c => c.id === catId)!.defaultBudget));
    setModal("editCatBudget");
  }

  function saveEditCat() {
    const budget = Math.max(0, parseFloat(editCatAmt) || 0);
    const prev   = cats[editCatId] ?? { budget: 0 };
    persistMonth({ categories: { ...cats, [editCatId]: { ...prev, budget } } });
    setModal(null);
  }

  function openSetBudget() {
    const init: Record<string, string> = {};
    for (const c of BUDGET_CATS) {
      init[c.id] = String(cats[c.id]?.budget ?? c.defaultBudget);
    }
    setEditBudgets(init);
    setModal("setBudget");
  }

  function saveSetBudget() {
    const newCats: Record<string, any> = {};
    for (const c of BUDGET_CATS) {
      const budget = Math.max(0, parseFloat(editBudgets[c.id]) || 0);
      newCats[c.id] = { budget };
    }
    persistMonth({ categories: newCats });
    setModal(null);
  }

  function submitLogSpend() {
    const amt = parseFloat(logAmt);
    if (!amt || amt <= 0) return;
    const txn: Transaction = {
      id:       newId(),
      date:     todayFull,
      amount:   Math.round(amt * 100) / 100,
      category: logCat,
      merchant: logMerchant.trim() || "Manual entry",
      source:   "manual",
    };
    // Ensure category has a budget entry if missing
    const catBudget = cats[logCat]?.budget ?? BUDGET_CATS.find(c => c.id === logCat)!.defaultBudget;
    const updatedCats = cats[logCat] ? cats : { ...cats, [logCat]: { budget: catBudget } };
    persistMonth({ categories: updatedCats, transactions: [...transactions, txn] });
    setLogAmt(""); setLogMerchant("");
    setModal(null);
  }

  function deleteTransaction(id: string) {
    persistMonth({ transactions: transactions.filter(t => t.id !== id) });
  }

  const logAmtVal = parseFloat(logAmt) || 0;
  const incomeVal = parseFloat(incomeInput) || 0;
  const health    = monthData ? computeHealth(computedCats) : null;

  const STATUS_STYLE: Record<string, { bg: string; border: string; text: string; label: string; bar: string }> = {
    good:  { bg: "#052e16", border: "#16a34a", text: "#4ade80", label: "✅ On Track",      bar: "#16a34a" },
    close: { bg: "#422006", border: "#d97706", text: "#fbbf24", label: "⚠️ Getting Close", bar: "#d97706" },
    over:  { bg: "#450a0a", border: "#dc2626", text: "#f87171", label: "🚨 Over Budget",   bar: "#dc2626" },
  };

  const sortedTxns = [...transactions].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div style={{ fontFamily: "Nunito", background: "#0f172a", minHeight: "100vh", paddingBottom: 100 }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#1e3a5f,#0f172a)", padding: "24px 18px 20px", borderBottom: "1px solid #1e293b" }}>
        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 3, marginBottom: 4 }}>Monthly Budget</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", lineHeight: 1.1, marginBottom: 4 }}>📊 Budget vs. Actual</div>
        <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>Set targets. Track reality. Close the gap.</div>
      </div>

      <div style={{ padding: "16px" }}>

        {/* Month selector */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#1e293b", borderRadius: 16, padding: "10px 14px", marginBottom: 14 }}>
          <button onClick={() => setViewMonth(m => shiftMonth(m, -1))}
            style={{ background: "#334155", border: "none", borderRadius: 10, width: 36, height: 36, color: "#94a3b8", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One" }}>{getMonthLabel(viewMonth)}</div>
          <button onClick={() => setViewMonth(m => shiftMonth(m, 1))} disabled={viewMonth >= today}
            style={{ background: viewMonth >= today ? "#1e293b" : "#334155", border: "none", borderRadius: 10, width: 36, height: 36, color: viewMonth >= today ? "#334155" : "#94a3b8", fontSize: 18, cursor: viewMonth >= today ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
        </div>

        {/* Connect card banner — hidden until Plaid is live */}
        {plaidLinked && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#052e16", border: "1.5px solid #16a34a55", borderRadius: 14, padding: "12px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 22 }}>✅</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#4ade80" }}>Bank connected</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>Transactions synced for {getMonthLabel(viewMonth)}</div>
            </div>
            <button onClick={async () => { setPlaidBusy(true); await syncTransactions(); setPlaidBusy(false); }} disabled={plaidBusy}
              style={{ background: "#16a34a22", border: "1px solid #16a34a55", borderRadius: 10, padding: "6px 12px", color: "#4ade80", fontWeight: 900, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" as const }}>
              {plaidBusy ? "Syncing…" : "Re-sync"}
            </button>
          </div>
        )}

        {/* ── Monthly Income card ── */}
        <div style={{ background: "#1e293b", border: `1.5px solid ${income > 0 ? "#16a34a55" : "#334155"}`, borderRadius: 16, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 26 }}>💵</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 900, color: "#475569", letterSpacing: 1.5, textTransform: "uppercase" as const, marginBottom: 2 }}>Monthly Income</div>
                {income > 0
                  ? <div style={{ fontSize: 20, fontWeight: 900, color: "#4ade80", fontFamily: "Fredoka One" }}>{fmt(income)} <span style={{ fontSize: 12, color: "#475569", fontWeight: 700 }}>/ month</span></div>
                  : <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>Not set yet</div>}
              </div>
            </div>
            <button
              onClick={() => { setIncomeInput(income > 0 ? String(income) : ""); setModal("setIncome"); }}
              style={{ background: income > 0 ? "#16a34a22" : "linear-gradient(135deg,#0ea5e9,#8b5cf6)", border: income > 0 ? "1px solid #16a34a55" : "none", borderRadius: 10, padding: "8px 14px", color: income > 0 ? "#4ade80" : "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Fredoka One", whiteSpace: "nowrap" as const }}>
              {income > 0 ? "✏️ Edit" : "Set Income"}
            </button>
          </div>
          {income > 0 && health && (
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[
                { label: "Budgeted",  val: fmt(health.totalBudget), sub: `${pct(health.totalBudget, income)}%`, color: "#93c5fd" },
                { label: "Spent",     val: fmt(health.totalActual), sub: `${pct(health.totalActual, income)}%`, color: health.totalActual > income ? "#f87171" : "#fbbf24" },
                { label: "Remaining", val: fmt(income - health.totalActual), sub: "after spending",             color: income - health.totalActual >= 0 ? "#4ade80" : "#f87171" },
              ].map(s => (
                <div key={s.label} style={{ background: "#0f172a", borderRadius: 10, padding: "8px 6px", textAlign: "center" as const }}>
                  <div style={{ fontSize: 9, fontWeight: 900, color: "#475569", textTransform: "uppercase" as const, letterSpacing: 0.5, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: s.color, fontFamily: "Fredoka One" }}>{s.val}</div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#475569" }}>{s.sub}</div>
                </div>
              ))}
            </div>
          )}
          {!income && (
            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: "#475569" }}>
              Add your take-home pay to see how your spending compares to what you earn.
            </div>
          )}
        </div>

        {/* No budget set yet */}
        {!monthData && (
          <div style={{ background: "#1e293b", borderRadius: 20, padding: "32px 20px", textAlign: "center" as const, marginBottom: 14, border: "1px solid #334155" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", marginBottom: 6 }}>
              No budget for {getMonthLabel(viewMonth)}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 20 }}>
              {income > 0 ? `Income set at ${fmt(income)} — now allocate it across categories.` : "Set your monthly spending targets to start tracking."}
            </div>
            <button onClick={openSetBudget}
              style={{ background: "linear-gradient(135deg,#0ea5e9,#8b5cf6)", border: "none", borderRadius: 14, padding: "13px 28px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One" }}>
              Set Up This Month 🎯
            </button>
          </div>
        )}

        {/* Health summary card */}
        {health && (() => {
          const s = STATUS_STYLE[health.status];
          return (
            <div style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 18, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: s.text, fontFamily: "Fredoka One" }}>{s.label}</div>
                <div style={{ fontSize: 13, fontWeight: 900, color: s.text }}>{Math.round(health.pct)}% of budget used</div>
              </div>
              <div style={{ background: "#0f172a", borderRadius: 20, height: 8, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ width: `${Math.min(100, health.pct)}%`, height: "100%", background: s.bar, borderRadius: 20, transition: "width 0.6s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 800, color: "#64748b" }}>
                <span>Spent: <strong style={{ color: s.text }}>{fmt(health.totalActual)}</strong></span>
                <span>Budget: <strong style={{ color: "#94a3b8" }}>{fmt(health.totalBudget)}</strong></span>
                {health.status === "over"
                  ? <span style={{ color: "#f87171" }}>Over by {fmt(health.totalActual - health.totalBudget)}</span>
                  : <span style={{ color: "#4ade80" }}>{fmt(health.totalBudget - health.totalActual)} left</span>}
              </div>
            </div>
          );
        })()}

        {/* Category rows */}
        {monthData && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            {BUDGET_CATS.map(cat => {
              const budget    = cats[cat.id]?.budget ?? 0;
              const actual    = getCatActual(cat.id);
              const p         = budget > 0 ? (actual / budget) * 100 : 0;
              const over      = actual > budget && budget > 0;
              const barColor  = over ? "#dc2626" : p >= 85 ? "#d97706" : cat.color;
              const remaining = budget - actual;

              return (
                <div key={cat.id}
                  onClick={() => { setLogCat(cat.id); setLogAmt(""); setLogMerchant(""); setModal("logSpend"); }}
                  style={{ background: "#1e293b", borderRadius: 16, padding: "14px 16px", border: `1px solid ${over ? "#dc262633" : "#334155"}`, cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: budget > 0 ? 8 : 0 }}>
                    <div style={{ fontSize: 22, width: 32, textAlign: "center" as const }}>{cat.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 900, color: "#f1f5f9" }}>{cat.label}</div>
                      {income > 0 && actual > 0 && (
                        <div style={{ fontSize: 10, fontWeight: 800, color: "#475569" }}>{pct(actual, income)}% of income</div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ textAlign: "right" as const }}>
                        <div style={{ fontSize: 14, fontWeight: 900, color: over ? "#f87171" : "#f1f5f9" }}>
                          {fmt(actual)} <span style={{ color: "#475569", fontSize: 12 }}>/ {fmt(budget)}</span>
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 800, color: over ? "#f87171" : remaining > 0 ? "#4ade80" : "#94a3b8" }}>
                          {budget === 0 ? "no budget set" : over ? `${fmt(Math.abs(remaining))} over` : `${fmt(remaining)} left`}
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); openEditCat(cat.id); }}
                        style={{ background: "#334155", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13 }}>
                        ✏️
                      </button>
                    </div>
                  </div>
                  {budget > 0 && (
                    <div style={{ background: "#0f172a", borderRadius: 20, height: 7, overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(100, p)}%`, height: "100%", background: barColor, borderRadius: 20, transition: "width 0.5s" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action buttons */}
        {monthData && (
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <button onClick={() => { setLogCat(BUDGET_CATS[0].id); setLogAmt(""); setLogMerchant(""); setModal("logSpend"); }}
              style={{ flex: 1, background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 14, padding: "13px", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One" }}>
              + Log a Spend
            </button>
            <button onClick={openSetBudget}
              style={{ background: "#1e293b", border: "1.5px solid #334155", borderRadius: 14, padding: "13px 16px", color: "#94a3b8", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One" }}>
              Edit All
            </button>
          </div>
        )}

        {/* Transaction history */}
        {sortedTxns.length > 0 && (
          <div style={{ background: "#1e293b", borderRadius: 16, padding: "14px 16px", marginBottom: 14, border: "1px solid #334155" }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: "#94a3b8", marginBottom: 12, textTransform: "uppercase" as const, letterSpacing: 1.5 }}>
              Recent Transactions
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sortedTxns.map(txn => {
                const cat = BUDGET_CATS.find(c => c.id === txn.category);
                const txnDate = new Date(txn.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
                return (
                  <div key={txn.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #0f172a" }}>
                    <div style={{ fontSize: 20, width: 28, textAlign: "center" as const, flexShrink: 0 }}>{cat?.emoji ?? "📦"}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{txn.merchant}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>{cat?.label ?? txn.category} · {txnDate}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: "#f1f5f9", flexShrink: 0 }}>{fmtFull(txn.amount)}</div>
                    <button onClick={() => deleteTransaction(txn.id)}
                      style={{ background: "none", border: "none", color: "#475569", fontSize: 16, cursor: "pointer", padding: "4px", flexShrink: 0, lineHeight: 1 }}>×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pro tip */}
        <div style={{ background: "#1e293b", borderRadius: 14, padding: "12px 14px", border: "1px solid #334155" }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: "#0ea5e9", marginBottom: 4 }}>💡 Pro Tip</div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, lineHeight: 1.5 }}>
            {income > 0 && health
              ? health.totalActual > income
                ? `🚨 You've spent ${fmt(health.totalActual - income)} more than you earn this month. Cut something now.`
                : health.totalBudget > income
                  ? `⚠️ Your budgeted total (${fmt(health.totalBudget)}) exceeds your income (${fmt(income)}). Trim some categories.`
                  : `The 50/30/20 rule: 50% needs, 30% wants, 20% savings. You've used ${pct(health.totalActual, income)}% of your income so far.`
              : "The 50/30/20 rule: 50% on needs (housing, food, transport), 30% on wants (entertainment), 20% on savings. How does your budget stack up?"}
          </div>
        </div>
      </div>

      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={() => setModal(null)}>
          <div style={{ background: "#1e293b", borderRadius: "24px 24px 0 0", padding: "24px 20px 48px", width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: "#334155", borderRadius: 4, margin: "0 auto 20px" }} />

            {/* ── Set / Edit Income ── */}
            {modal === "setIncome" && <>
              <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Fredoka One", color: "#f1f5f9", marginBottom: 4 }}>
                💵 {income > 0 ? "Update" : "Set"} Monthly Income
              </div>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>{getMonthLabel(viewMonth)}</div>
              <div style={{ background: "#0f172a", borderRadius: 12, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#475569", fontWeight: 700, lineHeight: 1.5 }}>
                Enter your net take-home pay — what actually hits your bank account after taxes. Update it any time your income changes.
              </div>
              {income > 0 && (
                <div style={{ background: "#0f2d1a", border: "1px solid #16a34a44", borderRadius: 12, padding: "10px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#4ade80" }}>Current income</span>
                  <span style={{ fontSize: 15, fontWeight: 900, color: "#4ade80", fontFamily: "Fredoka One" }}>{fmt(income)}</span>
                </div>
              )}
              <input type="number" value={incomeInput} onChange={e => setIncomeInput(e.target.value)} placeholder="0"
                autoFocus
                style={{ width: "100%", background: "#0f172a", border: "1.5px solid #16a34a", borderRadius: 12, padding: "12px 14px", fontSize: 36, fontFamily: "Fredoka One", fontWeight: 900, color: "#4ade80", textAlign: "center" as const, outline: "none", boxSizing: "border-box" as const, marginBottom: 16 }} />
              <button onClick={saveIncome} disabled={incomeVal <= 0}
                style={{ width: "100%", background: incomeVal > 0 ? "linear-gradient(135deg,#16a34a,#15803d)" : "#334155", border: "none", borderRadius: 14, padding: "14px", color: incomeVal > 0 ? "#fff" : "#64748b", fontWeight: 900, fontSize: 16, cursor: incomeVal > 0 ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Save Income ✓
              </button>
            </>}

            {/* ── Edit single category budget ── */}
            {modal === "editCatBudget" && (() => {
              const cat      = BUDGET_CATS.find(c => c.id === editCatId)!;
              const catSpent = getCatActual(editCatId);
              const newAmt   = parseFloat(editCatAmt) || 0;
              return <>
                <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Fredoka One", color: "#f1f5f9", marginBottom: 4 }}>
                  {cat?.emoji} Edit {cat?.label} Budget
                </div>
                <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 16 }}>{getMonthLabel(viewMonth)}</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: "#0f172a", borderRadius: 12, padding: "10px 14px", textAlign: "center" as const }}>
                    <div style={{ fontSize: 10, fontWeight: 900, color: "#475569", textTransform: "uppercase" as const, letterSpacing: 1, marginBottom: 4 }}>Spent so far</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One" }}>{fmt(catSpent)}</div>
                  </div>
                  <div style={{ flex: 1, background: "#0f172a", borderRadius: 12, padding: "10px 14px", textAlign: "center" as const }}>
                    <div style={{ fontSize: 10, fontWeight: 900, color: "#475569", textTransform: "uppercase" as const, letterSpacing: 1, marginBottom: 4 }}>Current budget</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: cat?.color, fontFamily: "Fredoka One" }}>{fmt(cats[editCatId]?.budget ?? 0)}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", marginBottom: 8 }}>NEW BUDGET</div>
                <input type="number" value={editCatAmt} onChange={e => setEditCatAmt(e.target.value)} placeholder="0" autoFocus
                  style={{ width: "100%", background: "#0f172a", border: `1.5px solid ${cat?.color ?? "#334155"}`, borderRadius: 12, padding: "12px 14px", fontSize: 32, fontFamily: "Fredoka One", fontWeight: 900, color: cat?.color, textAlign: "center" as const, outline: "none", boxSizing: "border-box" as const, marginBottom: 8 }} />
                {newAmt > 0 && newAmt < catSpent && (
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#f87171", marginBottom: 12, textAlign: "center" as const }}>
                    ⚠️ New budget is below what you've already spent ({fmt(catSpent)})
                  </div>
                )}
                <button onClick={saveEditCat} disabled={newAmt <= 0}
                  style={{ width: "100%", background: newAmt > 0 ? `linear-gradient(135deg,${cat?.color ?? "#0ea5e9"},${cat?.color ?? "#8b5cf6"}99)` : "#334155", border: "none", borderRadius: 14, padding: "14px", color: newAmt > 0 ? "#fff" : "#64748b", fontWeight: 900, fontSize: 16, cursor: newAmt > 0 ? "pointer" : "default", fontFamily: "Fredoka One", marginBottom: 12 }}>
                  Save Budget ✓
                </button>
                <button onClick={() => setModal(null)}
                  style={{ width: "100%", background: "none", border: "none", color: "#475569", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "Nunito" }}>
                  Cancel
                </button>
              </>;
            })()}

            {/* ── Log a Spend ── */}
            {modal === "logSpend" && <>
              <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Fredoka One", color: "#f1f5f9", marginBottom: 4 }}>💸 Log a Spend</div>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 16 }}>{getMonthLabel(viewMonth)}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", marginBottom: 8 }}>CATEGORY</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 16 }}>
                {BUDGET_CATS.map(cat => (
                  <button key={cat.id} onClick={() => setLogCat(cat.id)}
                    style={{ background: logCat === cat.id ? cat.color + "33" : "#0f172a", border: `1.5px solid ${logCat === cat.id ? cat.color : "#334155"}`, borderRadius: 12, padding: "8px 4px", cursor: "pointer", textAlign: "center" as const }}>
                    <div style={{ fontSize: 20 }}>{cat.emoji}</div>
                    <div style={{ fontSize: 9, fontWeight: 900, color: logCat === cat.id ? cat.color : "#64748b", marginTop: 2 }}>{cat.label}</div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", marginBottom: 8 }}>AMOUNT</div>
              <input type="number" value={logAmt} onChange={e => setLogAmt(e.target.value)} placeholder="0.00" autoFocus
                style={{ width: "100%", background: "#0f172a", border: "1.5px solid #334155", borderRadius: 12, padding: "12px 14px", fontSize: 28, fontFamily: "Fredoka One", fontWeight: 900, color: "#10b981", textAlign: "center" as const, outline: "none", boxSizing: "border-box" as const, marginBottom: 12 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", marginBottom: 8 }}>WHERE? (optional)</div>
              <input value={logMerchant} onChange={e => setLogMerchant(e.target.value)} placeholder="Chipotle, Shell, Amazon…"
                style={{ width: "100%", background: "#0f172a", border: "1.5px solid #334155", borderRadius: 12, padding: "10px 14px", fontSize: 14, fontFamily: "Nunito", fontWeight: 700, color: "#f1f5f9", outline: "none", boxSizing: "border-box" as const, marginBottom: 16 }} />
              <button onClick={submitLogSpend} disabled={logAmtVal <= 0}
                style={{ width: "100%", background: logAmtVal > 0 ? "linear-gradient(135deg,#10b981,#059669)" : "#334155", border: "none", borderRadius: 14, padding: "14px", color: logAmtVal > 0 ? "#fff" : "#64748b", fontWeight: 900, fontSize: 16, cursor: logAmtVal > 0 ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Log {logAmtVal > 0 ? fmtFull(logAmtVal) : ""} 💸
              </button>
            </>}

            {/* ── Edit All Budgets ── */}
            {modal === "setBudget" && <>
              <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Fredoka One", color: "#f1f5f9", marginBottom: 4 }}>🎯 Edit Monthly Budgets</div>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 16 }}>{getMonthLabel(viewMonth)}</div>
              {income > 0 && (
                <div style={{ background: "#0f2d1a", border: "1px solid #16a34a33", borderRadius: 12, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#4ade80" }}>💵 Monthly income</span>
                  <span style={{ fontSize: 15, fontWeight: 900, color: "#4ade80", fontFamily: "Fredoka One" }}>{fmt(income)}</span>
                </div>
              )}
              {BUDGET_CATS.map(cat => {
                const catSpent = getCatActual(cat.id);
                return (
                  <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 24, width: 32 }}>{cat.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#f1f5f9", marginBottom: 2 }}>{cat.label}</div>
                      {catSpent > 0 && (
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>Spent: {fmt(catSpent)}</div>
                      )}
                    </div>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, fontWeight: 900, color: cat.color, fontFamily: "Fredoka One" }}>$</span>
                      <input type="number" value={editBudgets[cat.id] ?? ""} onChange={e => setEditBudgets(prev => ({ ...prev, [cat.id]: e.target.value }))}
                        style={{ width: 90, background: "#0f172a", border: `1.5px solid ${cat.color}55`, borderRadius: 10, padding: "8px 8px 8px 22px", fontSize: 15, fontFamily: "Fredoka One", fontWeight: 900, color: cat.color, outline: "none", boxSizing: "border-box" as const }} />
                    </div>
                  </div>
                );
              })}
              {(() => {
                const total      = BUDGET_CATS.reduce((s, c) => s + (parseFloat(editBudgets[c.id]) || 0), 0);
                const overIncome = income > 0 && total > income;
                return (
                  <div style={{ background: "#0f172a", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: income > 0 ? 6 : 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 900, color: "#94a3b8" }}>Total budgeted</span>
                      <span style={{ fontSize: 15, fontWeight: 900, color: overIncome ? "#f87171" : "#f1f5f9", fontFamily: "Fredoka One" }}>{fmt(total)}</span>
                    </div>
                    {income > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: "#475569" }}>Remaining income</span>
                        <span style={{ fontSize: 13, fontWeight: 900, color: overIncome ? "#f87171" : "#4ade80", fontFamily: "Fredoka One" }}>
                          {overIncome ? "−" : "+"}{fmt(Math.abs(income - total))}
                        </span>
                      </div>
                    )}
                    {overIncome && (
                      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: "#f87171" }}>
                        ⚠️ Budget exceeds income by {fmt(total - income)} — reduce some categories
                      </div>
                    )}
                  </div>
                );
              })()}
              <button onClick={saveSetBudget}
                style={{ width: "100%", background: "linear-gradient(135deg,#0ea5e9,#8b5cf6)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "Fredoka One" }}>
                Save Budgets ✓
              </button>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
